/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { BaseButton, Grid, Icon, isBlank, Spacer } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { doo } from "@cd2/tools"
import React, { useMemo, useRef, useState } from "react"
import { EOSPagination } from "../Components/EOSPagination"
import { useGlobalState } from "../Components/GlobalStateProvider"
import { IndexFilterWrapper } from "../Components/IndexFiltersWrapper"
import { PageWrapper } from "../Components/PageWrapper"
import { UserSelectList } from "../Components/SelectLists/UserSelectList"
import { SortSelect } from "../Components/SortSelect"
import { GoodsReceiptTable } from "../Components/Tables/GoodsReceiptTable"
import { IOrder, IOrderFilters, skeemSortForOrdersTable } from "../Components/Tables/OrdersTable"
import { NoResultsAdvanced, NoResultsBlock } from "../Components/Tables/utils"
import { TableWrapper } from "../Components/TableWrapper"
import { icons } from "../constants/icons"
import { pollingIntervals } from "../constants/pollingIntervals"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { getInternalOrderFriendlyStatusName } from "../hooks/useExportInternalOrder"
import { useIndexTableState } from "../hooks/useIndexTableState"
import { usePollingInterval } from "../hooks/useInterval"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { ILoadingStatus } from "../types/common"
import { useSettings } from "../utils/useSettings"
import {
  loadSourceOrderDespatchDates,
  sentStockTransfersAttribute,
  sourceOrderDespatchAttribute,
} from "../utils/sourceOrderDespatchDates"
import { useUpdateTransferIdDialog } from "./GoodsReceiptsPage"
import { useReloadKey } from "../hooks/useReloadKey"
import { CustomerSelectList2 } from "../Components/SelectLists/CustomerSelectList2"
import { buttonConfigs } from "../constants/buttonConfigs"

const defaultSort = { by: "updated_at", direction: "DESC" } as const

export enum InternalOrderBarscanStatus {
  "exporting_source" = "exporting_source",
  "failed_source" = "failed_source",
  "exported" = "exported",
  "exporting_target" = "exporting_target",
  "failed_target" = "failed_target",
  "complete" = "complete",
  "cancelled" = "cancelled",
  "partially_exported" = "partially_exported",
}
export enum InternalOrderNoBarscanStatus {
  "picking" = "picking",
  "despatched" = "despatched",
  "exported" = "complete",
}
export type IInternalOrderNoBarscanStatus = keyof typeof InternalOrderNoBarscanStatus

export function GoodsReceiptsIndexPage() {
  const [filter, setFilter] = useIndexTableState<
    IOrderFilters & {
      status?: InternalOrderBarscanStatus
      showCompleteRma?: boolean
    }
  >("goods-receipts-index", {
    query: "",
    productQuery: "",
    status: undefined,
    sort: defaultSort,
    pagination: { page: 1, perPage: 20 },
    archived: false,
    customer: undefined,
    showCompleteRma: false,
  })

  return (
    <React.Fragment>
      <PageWrapper
        breadcrumbPath={["goodsReceipts"]}
        title="Goods Receipts"
        subtitle={<React.Fragment></React.Fragment>}
      >
        <OrdersList includeArchived={!!filter.archived} filter={filter} setFilter={setFilter} />
      </PageWrapper>
    </React.Fragment>
  )
}

interface IListProps {
  includeArchived: boolean
  filter: IOrderFilters & {
    status?: InternalOrderBarscanStatus | InternalOrderNoBarscanStatus
    showCompleteRma?: boolean
  }
  setFilter: any
}

interface IFetchResult {
  records: (IOrder & { barscan_despatched_at?: Date | null })[]
  count: number
  rawCount: any
}

function asOption(value: string) {
  return {
    label: getInternalOrderFriendlyStatusName(value),
    value: value,
  }
}
const statusOptions = [
  { label: "All Incomplete", value: "incomplete" },
  { label: "All Complete", value: "complete" },
  {
    label: "Handled Interanlly",
    value: "non_barscan",
    options: [InternalOrderNoBarscanStatus.despatched, InternalOrderNoBarscanStatus.exported].map(
      asOption,
    ),
  },
  {
    label: "Exported Via Barscan",
    value: "barscan",
    options: [
      "exported",
      "exporting_target",
      "partially_exported",
      "complete",
      "failed_target",
      "cancelled",
    ].map(asOption),
  },
]

function statusFilter(status: string) {
  if (status === "incomplete") {
    return {
      matches: {
        internal_branch_transfer_status: {
          or: [
            InternalOrderBarscanStatus.exported,
            InternalOrderBarscanStatus.partially_exported,
            InternalOrderNoBarscanStatus.despatched,
            InternalOrderBarscanStatus.failed_source,
          ],
        },
      },
    }
  }
  if (status === "barscan" || status === "non_barscan") {
    return {
      matches: {
        internal_branch_transfer_status: {
          or: statusOptions.find(x => status === x.value)?.options?.map(y => y.value),
        },
      },
    }
  }
  return { matches: { internal_branch_transfer_status: status } }
}

function OrdersList({ includeArchived, filter, setFilter }: IListProps) {
  const alerts = useSiteAlerts()
  const settings = useSettings()
  const [status, setStatus] = useState<ILoadingStatus>("loading")
  const [totalCount, setTotalCount] = useState(0)
  const [rawCount, setRawCount] = useState(0)
  const debounceRef = useRef<any>(null)
  const { currentUser } = useGlobalState()
  const skeem = window.skeem

  const [orders, setOrders] = useState<IOrder[] | null>(null)
  const [dispatchedDates, setDispatchedDates] = useState(new Map<string, Date | null>())

  const { query, sort, pagination, customer, productQuery } = filter

  const [forkedQuery, setForkedQuery] = useState(query)
  const [forkedProductQuery, setForkedProductQuery] = useState(query)

  const paginationProps = useMemo(
    () => ({
      currentPage: pagination.page,
      gotoPage: (page: number) =>
        setFilter({ pagination: { perPage: filter.pagination.perPage, page } }),
      perPage: pagination.perPage,
      setPerPage: (perPage: number) => setFilter({ pagination: { page: 1, perPage } }),
      maxPages: Math.ceil((orders ? totalCount : 0) / pagination.perPage),
    }),
    [pagination.page, pagination.perPage, orders ? totalCount : 0],
  )

  const fetchFilter = ({ query, productQuery }: { query: string; productQuery: string }) => {
    let fetchStatusFilter = undefined
    if (filter.status && !query) {
      fetchStatusFilter = statusFilter(filter.status)
    }
    // hide by default, show if enabled
    const showCompleteRmaFilter = filter.showCompleteRma
      ? null
      : {
          or: [
            // not an RMA
            { matches: { return_request_source_rma: null, type: { not: "return_request" } } },
            // Is an and not complete
            {
              and: [
                {
                  or: [
                    { matches: { return_request_source_rma: { not: null } } },
                    { matches: { type: "return_request" } },
                  ],
                },
                {
                  not: {
                    matches: {
                      internal_branch_transfer_status: "complete",
                    },
                  },
                },
              ],
            },
          ],
        }
    return {
      and: [
        showCompleteRmaFilter,
        fetchStatusFilter,
        {
          matches: {
            internal_transfer_to_branch: currentUser.branch.id,
          },
        },
        filter.customer && !query && { matches: { customer: filter.customer.id } },
        filter.user && !query && { matches: { user: filter.user } },
        query && {
          or: [
            doo(() => {
              try {
                const value = Number(query)
                if (isNaN(value)) {
                  return null
                }
                if (!isFinite(value)) {
                  return null
                }
                return { matches: { barscanInternalOrderTransferId: value } }
              } catch {
                return null
              }
            }),
            { matches: { identifier: { like: query } } },
            { matches: { purchase_order_reference: { like: query } } },
            {
              gte: [
                {
                  count: {
                    path: [
                      {
                        name: "return_request_source_rma",
                        filter: { matches: { identifier: { like: query } } },
                      },
                    ],
                  },
                },
                { value: 1 },
              ],
            },
            { matches: { sage_order_number: { like: query } } },
            { eq: [{ path: ["address", "postcode"] }, { value: query }] },
            {
              exists: {
                path: [
                  {
                    name: "invoice_items",
                    filter: { matches: { invoice_document_number: { like: query } } },
                  },
                ],
              },
            },
          ].filter(Boolean),
        },
        productQuery && {
          gt: [
            {
              count: {
                path: [
                  "items",
                  {
                    name: "product",
                    filter: {
                      or: [
                        { matches: { stock_code: { like: productQuery } } },
                        { matches: { manufacturerCode: { like: productQuery } } },
                      ],
                    },
                  },
                ],
              },
            },
            { value: 0 },
          ],
        },
      ].filter(Boolean),
    }
  }

  async function fetchOrders({
    query,
    pagination,
    productQuery,
  }: {
    query: string
    pagination: { currentPage: number; perPage: number }
    productQuery: string
  }): Promise<IFetchResult | null> {
    let fetchResult
    try {
      fetchResult = await skeem.fetch2<IFetchResult>({
        obj: {
          records: {
            model: {
              name: "orders",
              attributes: [
                "archived",
                "delivery_method",
                "identifier",
                "pdf_generated",
                "purchase_order_reference",
                "processed",
                "sage_export_status",
                "sage_order_number",
                "updated_at",
                "created_at",
                "notes",
                "carriage_pennies",
                "acknowledgementEmailSentAt",
                "consolidated_orders_key",
                "internal_branch_transfer_status",
                "barscanInternalOrderTransferId",
                "barscan_despatched_at",
                { name: "user", attributes: ["name"] },
                { name: "internal_transfer_to_branch", attributes: ["name"] },
                { name: "customer", attributes: ["name", "default_sage_tax_rate_id", "tax_rate"] },
                { name: "quote", attributes: ["identifier"] },
                { name: "branch", attributes: ["name"] },
                { name: "delivery", attributes: [] },
                { name: "childOrder", attributes: ["identifier"] },
                { name: "parentOrder", attributes: ["identifier"] },
                { name: "items", attributes: ["price_pennies", "quantity", "discountPercent"] },
                { name: "return_request_source_rma", attributes: ["identifier"] },
                sourceOrderDespatchAttribute,
                sentStockTransfersAttribute,
              ],
              paginate: { page: pagination.currentPage, size: pagination.perPage },
              filter: fetchFilter({ query, productQuery }),
              sort: skeemSortForOrdersTable(filter),
            },
          },
          count: {
            count: {
              name: "orders",
              filter: fetchFilter({ query, productQuery }),
            },
          },
          rawCount: {
            count: { name: "orders", filter: { matches: { branch: currentUser.branch.id } } },
          },
        },
      })
      return fetchResult
    } catch (e) {
      console.error("fetching orders error", e)
      alerts.genericError("Error fetching orders, please try again")
      return null
    }
  }

  const [refetchKey, triggerReload] = useReloadKey()
  usePollingInterval(() => triggerReload(), pollingIntervals.intervalTime)

  async function loadRecords(isAlive: boolean) {
    if (!settings._loaded) {
      return
    }
    if (status === "done") {
      setStatus("reFetching")
    }
    const fetchResult = await fetchOrders({
      query,
      pagination: paginationProps,
      productQuery,
    })
    const fetchedDispatchedDates = fetchResult
      ? await loadSourceOrderDespatchDates(fetchResult.records)
      : null
    if (fetchResult && fetchedDispatchedDates && isAlive) {
      setDispatchedDates(fetchedDispatchedDates)
      setOrders(fetchResult.records)
      setTotalCount(fetchResult.count)
      setRawCount(fetchResult.rawCount)
    }
    setStatus("done")
  }

  useAsyncEffect(
    async ({ isAlive }) => {
      await loadRecords(isAlive())
    },
    [
      includeArchived,
      JSON.stringify(pagination),
      JSON.stringify(customer),
      filter.user,
      filter.showCompleteRma,
      sort,
      query,
      refetchKey,
      productQuery,
      filter.status,
      settings._loaded,
    ],
  )

  function handleSearchChange(query: string) {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    setForkedQuery(query)
    debounceRef.current = setTimeout(() => {
      setFilter({
        query,
        sort: isBlank(query) ? defaultSort : { by: "recommended", direction: "desc" },
      })
    }, 300)
  }

  function handleSearchProductChange(productQuery: string) {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    setForkedProductQuery(productQuery)
    debounceRef.current = setTimeout(() => {
      setFilter({
        productQuery,
      })
    }, 300)
  }

  const searchPending =
    forkedQuery !== query || forkedProductQuery !== productQuery || status === "reFetching"
  const tableStatus =
    status === "loading"
      ? "loading"
      : status === "reFetching"
      ? "reloading"
      : (totalCount === 0 && query !== "") || rawCount === 0
      ? "noResultsInFilters"
      : totalCount === 0
      ? "noResults"
      : "done"

  const triggerUpdateTransferId = useUpdateTransferIdDialog()
  const handleSetTransferId = useAsyncCallback(async (orderId: string) => {
    const updated = await triggerUpdateTransferId({ orderId })
    if (updated) {
      triggerReload()
    }
  })

  return (
    <React.Fragment>
      <Grid gap={12}>
        <div
          css={css`
            gap: 8px;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: end;
          `}
        >
          <IndexFilterWrapper>
            <Fields.Search
              label="Search"
              value={forkedQuery}
              onChange={handleSearchChange}
              suffix={
                <Icon icon={searchPending ? icons.spinner : icons.search} spin={searchPending} />
              }
            />
            <Spacer />
            <Fields.Search
              label="Search by Product Code"
              value={forkedProductQuery}
              onChange={handleSearchProductChange}
              suffix={
                <Icon icon={searchPending ? icons.spinner : icons.search} spin={searchPending} />
              }
            />
            <Spacer />
            <UserSelectList
              onChange={user => setFilter({ user })}
              value={filter.user}
              branchId={currentUser?.branch?.id || undefined}
              filter={{ matches: { userAccountType: "branch" } }}
              isClearable
            />
            <Spacer />
            <CustomerSelectList2
              label="Filter by Customer"
              value={filter.customer}
              onChange={(_, customer) => setFilter({ customer })}
              allBranches
              includeBranchName
              isClearable
            />
            <Spacer />
            <Fields.Select2
              label="Status"
              onChange={status => setFilter({ status: status?.value ?? undefined })}
              options={statusOptions}
              value={doo(() => {
                const group = statusOptions.find(v =>
                  v.options?.find(o => o.value === filter.status),
                )
                return (
                  group?.options?.find(o => o.value === filter.status) ??
                  statusOptions.find(x => x.value === filter.status)
                )
              })}
              isClearable
            />
            <Spacer />
            <SortSelect
              sortFields={[
                query && "recommended",
                "identifier",
                "internal_branch_transfer_status",
                "customer",
                "quote",
                "purchase_order_reference",
                "delivery_method",
                "updated_at",
              ]}
              onChange={value => setFilter({ ...filter, sort: { direction: "ASC", by: value } })}
              sort={filter.sort}
            />
          </IndexFilterWrapper>
          <EOSPagination {...paginationProps} />
        </div>
        <div>
          <BaseButton
            {...(filter.showCompleteRma
              ? buttonConfigs.toggleButtonActive
              : buttonConfigs.toggleButtonInactive)}
            onClick={() => setFilter({ ...filter, showCompleteRma: !filter.showCompleteRma })}
          >
            Display complete Returns
          </BaseButton>
        </div>
        <TableWrapper
          status={tableStatus}
          renderNoResults={() => <NoResultsBlock />}
          renderNoResultsInFilters={() => (
            <NoResultsAdvanced
              rawCount={rawCount}
              noDataMessage={"There are no internal orders."}
              actions={[]}
            />
          )}
          showTopScrollbar
        >
          <GoodsReceiptTable
            data={orders}
            dispatchedDates={dispatchedDates}
            sort={sort}
            onSortClick={sort => setFilter({ ...filter, sort })}
            columns={[
              "identifier",
              "transferId",
              "internal_branch_transfer_status",
              "branch",
              "customer",
              "purchaseOrderReference",
              "deliveryMethod",
              "totalPrice",
              "date",
              "dispatchedDate",
            ]}
            menuActions={row => [
              {
                children: "Set transfer ID",
                onClick: () => handleSetTransferId(row.id),
                disabled: handleSetTransferId.running,
              },
            ]}
          />
        </TableWrapper>
        <EOSPagination {...paginationProps} />
      </Grid>
    </React.Fragment>
  )
}
